import log from '@/lib/logger';
import {useEffect, useMemo, useState} from 'react';
import {Dialog, DialogActions, DialogBody, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Input} from '@/components/tailwind/input';
import {Description, Field, Fieldset, Label, Legend} from "@/components/tailwind/fieldset.tsx";
import {z} from 'zod';
import {v4 as uuid} from 'uuid';
import {useServicesTranslation} from '@/i18n/hooks';
import ValidationAlert from "@/components/validation-alert.tsx";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {createService, updateService} from "@/services/service-services.ts";
import {useAuth} from "@/context/use-auth.tsx";
import {Service, ServiceDTO, ServiceGroup} from "@/models/service.ts";
import {Textarea} from "@/components/tailwind/textarea.tsx";
import {ValidationIssue} from "@/types/problem-details.ts";
import {Checkbox, CheckboxField, CheckboxGroup} from "@/components/tailwind/checkbox.tsx";
import {Text} from "@/components/tailwind/text.tsx";
import * as Headless from '@headlessui/react'
import {useToast} from "@/hooks/use-toast.ts";

const serviceSchema = z.object({
    serviceName: z.string().min(1, {message: "serviceDialog.errors.name-required"}),
    description: z.string().optional(),
    // The input registers with valueAsNumber, so an empty field yields NaN (invalid_type) and any
    // value below 1 fails min — both map to the same required message.
    pointsPerUnit: z.number({error: "serviceDialog.errors.pointsPerUnit-required"})
        .min(1, {message: "serviceDialog.errors.pointsPerUnit-required"}),
    isFixedPoints: z.boolean(),
    isSpecialAssignment: z.boolean(),
});

type ServiceFormValues = z.infer<typeof serviceSchema>;

type ServiceDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    serviceGroup: ServiceGroup | undefined,
    service?: Service | undefined;
    title?: string;
}

export function ServiceDialog({
                                  isOpen,
                                  onClose,
                                  onSave,
                                  serviceGroup,
                                  service,
                                  title = service ? 'serviceDialog.title.edit' : 'serviceDialog.title.create'
                              }: ServiceDialogProps) {
    const {t} = useServicesTranslation();
    const {token} = useAuth()
    const {toast} = useToast()

    const defaultValues = useMemo(() => ({
        serviceName: service?.name || '',
        description: service?.description || '',
        pointsPerUnit: service?.pointsPerUnit || 1,
        isFixedPoints: service?.isFixedPoints ?? false,
        isSpecialAssignment: service?.isSpecialAssignment ?? false,
    }), [
        service?.name,
        service?.description,
        service?.pointsPerUnit,
        service?.isFixedPoints,
        service?.isSpecialAssignment]);

    const {
        control,
        register,
        handleSubmit,
        setError,
        formState: {errors},
        reset,
    } = useForm<ServiceFormValues>({
        resolver: zodResolver(serviceSchema),
        defaultValues,
    });

    useEffect(() => {
        if (isOpen) {
            reset(defaultValues);
        }
    }, [isOpen, reset, defaultValues]);

    function showSuccessToast(serviceName: string) {
        let description: string

        if (service) {
            description = t("serviceDialog.toast.success.description.edit", {serviceName: serviceName})
        } else {
            description = t("serviceDialog.toast.success.description.create", {serviceName: serviceName})
        }

        toast({
            title: t("serviceDialog.toast.success.title"),
            description: description,
            variant: "default",
        })
    }

    const onSubmit: SubmitHandler<ServiceFormValues> = async (data) => {
        if (await saveData(data)) {
            reset();
            onSave();
            showSuccessToast(data.serviceName);
            onClose();
        }
    };

    async function saveData(data: ServiceFormValues): Promise<boolean> {
        const serviceDto: ServiceDTO = {
            id: service?.id || uuid(),
            serviceGroupId: serviceGroup?.id,
            name: data.serviceName,
            description: data.description,
            pointsPerUnit: data.pointsPerUnit,
            isFixedPoints: data.isFixedPoints,
            isSpecialAssignment: data.isSpecialAssignment,
        }

        try {
            if (!service) {
                await createService(token, serviceDto)
            } else {
                await updateService(token, serviceDto)
            }

            return true
        } catch (error: any) {
            log.error("Error persisting service group", error)

            if (error.validationIssues) {
                error.validationIssues.forEach((finding: ValidationIssue) => {
                    if (finding.field in errors) {
                        setError(finding.field as keyof ServiceFormValues, {
                            message: finding.message
                        });
                    } else {
                        setError("root", {
                            message: finding.message
                        });
                    }
                });
            } else {
                if (error.status) {
                    setError("root", {
                        message: t("common.serverError.response-code-with-details", {
                            statusCode: error.status,
                            errorDetails: error.error
                        })
                    })
                } else {
                    setError("root", {
                        message: t("common.serverError.unknown")
                    })
                }
            }
            return false
        }
    }

    function getDialogTitle(serviceGroup: ServiceGroup | undefined): string {
        if (serviceGroup === undefined) {
            return t("serviceDialog.serviceWithoutGroupTitleMarker")
        } else {
            return t("serviceDialog.serviceGroupPrefix") + ": " + serviceGroup?.name
        }
    }

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>{t(title)} ({getDialogTitle(serviceGroup)})</DialogTitle>

            <DialogBody>
                {errors.root && (
                    <ValidationAlert message={errors.root?.message ||
                        t("common.serverError.unknown")}/>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="space-y-2">
                        <Field>
                            <Label>{t("serviceDialog.fields.name.label")}</Label>

                            <Input
                                {...register("serviceName")}
                                type="text"
                                placeholder={t('serviceDialog.fields.name.placeholder')}
                                invalid={!!errors.serviceName}
                                data-testid="service-name"
                            />
                        </Field>
                        {errors.serviceName && (
                            <ValidationAlert message={t(errors.serviceName?.message ||
                                "serviceDialog.errors.unknown-name-issue")}
                            />
                        )}
                    </div>

                    <div className="space-y-2">
                        <Field>
                            <Label>
                                {t("serviceDialog.fields.description.label")}
                            </Label>
                            <Textarea
                                {...register("description")}
                                placeholder={t('serviceDialog.fields.description.placeholder')}
                                invalid={!!errors.description}
                                data-testid="service-description"
                            />
                        </Field>
                        {errors.description && (
                            <ValidationAlert
                                message={t(errors.description?.message ||
                                    "serviceDialog.errors.unknown-description-issue")}
                            />
                        )}
                    </div>

                    <div className="space-y-2">
                        <Headless.Field className="flex items-center gap-6">
                            <Label className="w-[120px] flex-shrink-0">
                                {t("serviceDialog.fields.pointsPerUnit.label")}
                            </Label>
                            <Input
                                type="number"
                                min="1"
                                {...register("pointsPerUnit", {valueAsNumber: true})}
                                placeholder={t('serviceDialog.fields.pointsPerUnit.placeholder')}
                                invalid={!!errors.pointsPerUnit}
                                className="w-full"
                                data-testid="service-points"
                            />
                        </Headless.Field>
                        {errors.pointsPerUnit && (
                            <ValidationAlert
                                message={t(errors.pointsPerUnit?.message ||
                                    "serviceDialog.errors.unknown-pointsPerUnit-issue")}
                            />
                        )}
                    </div>

                    <Fieldset>
                        <Legend>{t("serviceDialog.flags.legend")}</Legend>
                        <Text>{t("serviceDialog.flags.text")}</Text>
                        <CheckboxGroup>
                            <CheckboxField>
                                <Controller
                                    name="isFixedPoints"
                                    control={control}
                                    render={({field}) => (
                                        <Checkbox
                                            checked={field.value}
                                            onChange={field.onChange}
                                            data-testid="service-fixed-points"
                                        />
                                    )}
                                />
                                <Label>
                                    {t("serviceDialog.fields.isFixedPoints.label")}
                                </Label>
                                <Description>
                                    {t("serviceDialog.fields.isFixedPoints.description")}
                                    {errors.isFixedPoints && (
                                        <ValidationAlert
                                            message={t(errors.isFixedPoints?.message ||
                                                "serviceDialog.errors.unknown-isFixedPoints-issue")}
                                        />
                                    )}
                                </Description>
                            </CheckboxField>

                            <CheckboxField>
                                <Controller
                                    name="isSpecialAssignment"
                                    control={control}
                                    render={({field}) => (
                                        <Checkbox
                                            checked={field.value}
                                            onChange={field.onChange}
                                            data-testid="service-special-assignment"
                                        />
                                    )}
                                />

                                <Label>
                                    {t("serviceDialog.fields.isSpecialAssignment.label")}
                                </Label>

                                <Description>
                                    {t("serviceDialog.fields.isSpecialAssignment.description")}
                                    {errors.isSpecialAssignment && (
                                        <ValidationAlert
                                            message={t(errors.isSpecialAssignment?.message ||
                                                "serviceDialog.errors.unknown-isSpecialAssignment-issue")}
                                        />
                                    )}
                                </Description>
                            </CheckboxField>

                        </CheckboxGroup>
                    </Fieldset>

                    <DialogActions>
                        <Button
                            plain
                            onClick={onClose}
                            aria-label={t("serviceDialog.buttons.cancel.ariaLabel")}
                        >
                            {t("serviceDialog.buttons.cancel.text")}
                        </Button>
                        <Button
                            type="submit"
                            aria-label={t("serviceDialog.buttons.submit.ariaLabel")}
                            data-testid="submit-service"
                        >
                            {t("serviceDialog.buttons.submit.text")}
                        </Button>
                    </DialogActions>
                </form>
            </DialogBody>
        </Dialog>
    );
}

export function useServiceDialog() {
    const [isOpen, setIsOpen] = useState(false);
    const [serviceGroup, setServiceGroup] = useState<ServiceGroup | undefined>(undefined)
    const [service, setService] = useState<Service | undefined>(undefined)

    function openDialog(serviceGroup?: ServiceGroup | undefined, service?: Service | undefined) {
        setServiceGroup(serviceGroup)
        setService(service)
        setIsOpen(true)
    }

    function closeDialog() {
        setIsOpen(false)
    }

    return {
        isServiceDialogOpen: isOpen,
        initialServiceGroupDialogData: serviceGroup,
        openServiceDialog: openDialog,
        closeServiceDialog: closeDialog,
        serviceDialogProps: {
            isOpen: isOpen,
            onClose: closeDialog,
            serviceGroup: serviceGroup,
            service: service
        }
    }
}
